import { useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';

import { CartItemDialogProps } from '@/components/modules/Application/components/Cart/DefaultCart/components/CartItemDialog/CartItemDialog';
import { useOnDialogOpen } from '@/components/modules/Application/components/Cart/DefaultCart/components/CartItemDialog/hooks/useOnDialogOpen';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { useCartItemState } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCartItemState';
import { useTrigger } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useTrigger';
import { FormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/FormItem';
import { useKeyboardActions } from '@/components/shared/hooks/useKeyboardActions';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';

export const useCartItemDialog = ({ index, items, cartFormActions, open, setOpen }: CartItemDialogProps) => {
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();
  const { cart, setCartItem } = useCart();
  const { isNewCartItem, selectableArticleTypes } = useCartItemState({ items, index });
  const {
    watch,
    clearErrors,
    handleSubmit,
    formState: { isValid: isFormValid },
  } = useFormContext();
  const currentFormItem = watch('item');

  useOnDialogOpen({ index, isNewCartItem, open });

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const { trigger: doSuccessLayout } = useTrigger({
    callback: () => {
      if (loadingSubmit) setLoadingSubmit(false);
      setSnackbarNotification({
        msg: 'CART_DIALOG_SUCCESS',
        level: 'success',
      });
      if (cart) updateCartFormItem(cart.items[index]);
      setOpen(false);
    },
  });

  const handleClose = () => {
    setConfirmationDialogOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    clearErrors();
    if (isNewCartItem) cartFormActions.removeItem(index);
    setConfirmationDialogOpen(false);
    setLoadingSubmit(false);
  };

  const updateCartItem = (data: FieldValues): Item<FormItem> => {
    const newCartItem = setCartItem(data.item, index);
    return newCartItem;
  };

  const updateCartFormItem = (newCartItem: Item<FormItem>) => {
    cartFormActions.updateItem(index, newCartItem.toMinimalFormItem());
  };

  const handleSave = async (): Promise<void> => {
    setLoadingSubmit(true);
    await handleSubmit(
      // onValid
      (data) => {
        updateCartItem(data);
        setTimeout(() => {
          doSuccessLayout();
        }, 300);
      },
      // onInvalid
      () => setLoadingSubmit(false),
    )();
  };

  const handleSaveAndAddItem = async (): Promise<void> => {
    await handleSubmit(
      // onValid
      (data) => {
        updateCartItem(data);
        setTimeout(() => {
          doSuccessLayout();
          setTimeout(() => {
            cartFormActions.appendItem();
          }, 300);
        }, 300);
      },
    )();
  };

  useKeyboardActions({
    enterAction: handleSave,
    escAction: handleClose,
    disable: !open || confirmationDialogOpen,
  });

  return {
    currentFormItem,
    onClose,
    handleClose,
    handleSave,
    handleSaveAndAddItem,
    loadingSubmit,
    confirmationDialogOpen,
    setConfirmationDialogOpen,
    selectableArticleTypes,
    isFormValid,
  };
};
